import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, Plane, Clock3, Luggage, ShieldCheck, RotateCcw, ArrowRight,
    SlidersHorizontal, Sparkles, X,
} from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Sections5";
import { fetchFlightSearch, fetchFareRules, revalidateFares, formatApiError } from "@/lib/api";

export const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
export const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
export const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
export const fmtDur = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;

const SORTS = [
    ["price", "Lowest price"], ["duration", "Shortest duration"], ["departure", "Earliest departure"],
];

function sortOffers(list, sort) {
    const arr = [...list];
    if (sort === "price") arr.sort((a, b) => a.fare.per_adult_total - b.fare.per_adult_total);
    if (sort === "duration") arr.sort((a, b) => a.duration_min - b.duration_min);
    if (sort === "departure") arr.sort((a, b) => a.segments[0].depart.localeCompare(b.segments[0].depart));
    return arr;
}

function OfferCard({ offer, selected, onSelect, onRules, testid }) {
    const first = offer.segments[0];
    const last = offer.segments[offer.segments.length - 1];
    return (
        <button type="button" onClick={() => onSelect(offer)} data-testid={testid}
            className={`w-full text-left rounded-2xl border bg-white p-4 sm:p-5 transition-all hover:shadow-lg ${selected ? "border-saffron ring-2 ring-saffron/40 shadow-lg" : "border-stone-200"}`}>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 justify-between">
                <div className="flex items-center gap-3 min-w-[130px]">
                    <span className="h-10 w-10 rounded-xl bg-navy text-cream font-display font-black flex items-center justify-center text-sm">{offer.airline_code}</span>
                    <div>
                        <p className="font-bold text-navy text-sm">{offer.airline}</p>
                        <p className="text-[11px] text-stone-400">{offer.flight_nos.join(" · ")}</p>
                    </div>
                </div>
                <div className="flex items-center gap-4 sm:gap-6">
                    <div className="text-center">
                        <p className="font-display font-black text-lg text-navy">{fmtTime(first.depart)}</p>
                        <p className="text-xs font-bold text-stone-500">{first.from}</p>
                    </div>
                    <div className="text-center min-w-[90px]">
                        <p className="text-[11px] text-stone-400 flex items-center justify-center gap-1"><Clock3 className="h-3 w-3" />{fmtDur(offer.duration_min)}</p>
                        <div className="relative my-1 h-px bg-stone-300">
                            <Plane className="absolute -top-2 left-1/2 -translate-x-1/2 h-3.5 w-3.5 text-saffron rotate-90" />
                        </div>
                        <p className="text-[11px] font-semibold text-stone-500">
                            {offer.stops === 0 ? "Non-stop" : `${offer.stops} stop${offer.stops > 1 ? "s" : ""} · via ${offer.segments.slice(0, -1).map((s) => s.to).join(", ")}`}
                        </p>
                    </div>
                    <div className="text-center">
                        <p className="font-display font-black text-lg text-navy">{fmtTime(last.arrive)}</p>
                        <p className="text-xs font-bold text-stone-500">{last.to}</p>
                    </div>
                </div>
                <div className="text-right">
                    <p className="font-display font-black text-2xl text-navy">{inr(offer.fare.per_adult_total)}</p>
                    <p className="text-[11px] text-stone-400">per adult · all taxes in</p>
                </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
                <span className="rounded-full bg-cream px-2.5 py-1 font-bold text-navy/70">{offer.fare_family}</span>
                <span className="rounded-full bg-cream px-2.5 py-1 font-semibold text-stone-500 flex items-center gap-1"><Luggage className="h-3 w-3" />{offer.baggage.checkin_kg}kg check-in + {offer.baggage.cabin_kg}kg cabin</span>
                {offer.refundable
                    ? <span className="rounded-full bg-green-100 px-2.5 py-1 font-bold text-green-700 flex items-center gap-1"><ShieldCheck className="h-3 w-3" />Refundable</span>
                    : <span className="rounded-full bg-stone-100 px-2.5 py-1 font-bold text-stone-500">Non-refundable</span>}
                {offer.changeable && <span className="rounded-full bg-blue-50 px-2.5 py-1 font-bold text-blue-600 flex items-center gap-1"><RotateCcw className="h-3 w-3" />Changes allowed</span>}
                <span role="button" tabIndex={0} data-testid={`${testid}-rules`}
                    onClick={(e) => { e.stopPropagation(); onRules(offer); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onRules(offer); } }}
                    className="ml-auto font-bold text-saffron hover:underline cursor-pointer">Fare rules</span>
            </div>
        </button>
    );
}

export default function FlightResultsPage() {
    const { searchId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [sort, setSort] = useState("price");
    const [filters, setFilters] = useState({ nonstop: false, refundable: false, airline: "" });
    const [selected, setSelected] = useState({}); // leg_index -> offer
    const [rules, setRules] = useState(null);
    const [rulesBusy, setRulesBusy] = useState(false);
    const [continuing, setContinuing] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        fetchFlightSearch(searchId)
            .then(setData)
            .catch((e) => setError(formatApiError(e.response?.data?.detail)));
    }, [searchId]);

    const airlines = useMemo(() => {
        if (!data) return [];
        const set = new Set();
        Object.values(data.offers_by_leg).flat().forEach((o) => set.add(o.airline));
        return [...set].sort();
    }, [data]);

    const applyFilters = (list) => list.filter((o) =>
        (!filters.nonstop || o.stops === 0)
        && (!filters.refundable || o.refundable)
        && (!filters.airline || o.airline === filters.airline));

    const legs = data?.legs || [];
    const allSelected = legs.length > 0 && legs.every((_, i) => selected[i]);
    const total = legs.reduce((sum, _, i) => sum + (selected[i]?.fare.per_adult_total || 0), 0);
    const paxCount = data ? data.pax.adults + data.pax.children + data.pax.infants : 0;

    const openRules = async (offer) => {
        setRulesBusy(true);
        try { setRules(await fetchFareRules(offer.id)); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setRulesBusy(false); }
    };

    const proceed = async () => {
        setContinuing(true);
        try {
            const offerIds = legs.map((_, i) => selected[i].id);
            const rv = await revalidateFares(offerIds);
            if (!rv.ok) {
                const bad = rv.results.find((r) => !r.ok);
                toast.error(bad?.message || "A fare changed — please review the results again");
                return;
            }
            sessionStorage.setItem("gc_flight_selection", JSON.stringify({ search_id: searchId, offer_ids: offerIds }));
            navigate(`/flights/checkout/${searchId}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally { setContinuing(false); }
    };

    if (error) {
        return (
            <div className="min-h-screen bg-cream"><Header />
                <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="results-error">
                    <p className="font-display font-black text-2xl text-navy">This search has expired</p>
                    <p className="mt-2 text-sm text-stone-500">{error}</p>
                    <Link to="/flights" className="mt-6 inline-block rounded-full bg-saffron px-6 py-3 font-bold text-white" data-testid="results-error-back">Start a new search</Link>
                </div>
            <Footer /></div>
        );
    }
    if (!data) {
        return <div className="min-h-screen bg-cream flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-saffron" data-testid="results-loading" /></div>;
    }

    return (
        <div className="min-h-screen bg-cream pb-32" data-testid="flight-results-page">
            <Header />
            <section className="bg-navy text-cream">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 justify-between">
                        <div>
                            <p className="font-script text-2xl text-gold">{data.trip_type.replace("_", " ")} · {data.cabin.replace("_", " ")}</p>
                            <h1 className="font-display font-black text-2xl sm:text-3xl">
                                {legs.map((l) => `${l.origin} → ${l.destination}`).join("   ·   ")}
                            </h1>
                            <p className="mt-1 text-sm text-cream/70">
                                {legs.map((l) => fmtDate(l.date + "T00:00:00")).join(" · ")} · {paxCount} traveller{paxCount > 1 ? "s" : ""}
                            </p>
                        </div>
                        <Link to="/flights" className="rounded-full border border-cream/40 px-5 py-2 text-sm font-bold hover:bg-cream hover:text-navy transition-colors" data-testid="modify-search">Modify search</Link>
                    </div>
                </div>
            </section>

            {data.demo && (
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="mt-4 rounded-xl border border-gold/60 bg-gold/15 px-4 py-3 text-sm text-navy flex gap-2 items-start" data-testid="demo-results-banner">
                        <Sparkles className="h-4 w-4 mt-0.5 text-saffron shrink-0" />
                        <span><b>Demonstration fares.</b> These results show how live flight inventory will look.
                            They are sample prices — not bookable offers — until our certified supplier goes live.</span>
                    </div>
                </div>
            )}

            <main className="max-w-6xl mx-auto px-4 sm:px-6 mt-6">
                <div className="flex flex-wrap items-center gap-3 mb-5">
                    <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}
                        className="rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-navy">
                        {SORTS.map(([k, l]) => <option key={k} value={k}>{`Sort: ${l}`}</option>)}
                    </select>
                    <button data-testid="filters-toggle" onClick={() => setShowFilters(!showFilters)}
                        className={`rounded-xl border px-3 py-2 text-sm font-semibold flex items-center gap-2 ${showFilters ? "border-saffron text-saffron" : "border-stone-300 text-navy"}`}>
                        <SlidersHorizontal className="h-4 w-4" /> Filters
                    </button>
                    {showFilters && (
                        <div className="flex flex-wrap items-center gap-2" data-testid="filters-panel">
                            <label className="flex items-center gap-1.5 text-xs font-bold text-navy bg-white border border-stone-300 rounded-full px-3 py-1.5 cursor-pointer">
                                <input type="checkbox" data-testid="filter-nonstop" checked={filters.nonstop}
                                    onChange={(e) => setFilters({ ...filters, nonstop: e.target.checked })} />Non-stop only
                            </label>
                            <label className="flex items-center gap-1.5 text-xs font-bold text-navy bg-white border border-stone-300 rounded-full px-3 py-1.5 cursor-pointer">
                                <input type="checkbox" data-testid="filter-refundable" checked={filters.refundable}
                                    onChange={(e) => setFilters({ ...filters, refundable: e.target.checked })} />Refundable
                            </label>
                            <select data-testid="filter-airline" value={filters.airline} onChange={(e) => setFilters({ ...filters, airline: e.target.value })}
                                className="rounded-full border border-stone-300 bg-white px-3 py-1.5 text-xs font-bold text-navy">
                                <option value="">All airlines</option>
                                {airlines.map((a) => <option key={a} value={a}>{a}</option>)}
                            </select>
                        </div>
                    )}
                </div>

                {legs.map((leg, i) => {
                    const list = sortOffers(applyFilters(data.offers_by_leg[String(i)] || []), sort);
                    const ap = data.airports || {};
                    return (
                        <section key={i} className="mb-10" data-testid={`leg-section-${i}`}>
                            <h2 className="font-display font-black text-xl text-navy mb-1">
                                {legs.length > 1 ? (data.trip_type === "round_trip" ? (i === 0 ? "Onward" : "Return") : `Flight ${i + 1}`) : "Flights"}
                                {"  —  "}{ap[leg.origin]?.city || leg.origin} → {ap[leg.destination]?.city || leg.destination}
                            </h2>
                            <p className="text-xs text-stone-500 mb-4">{fmtDate(leg.date + "T00:00:00")} · {list.length} option{list.length !== 1 ? "s" : ""}</p>
                            {!list.length && (
                                <div className="rounded-2xl border border-stone-200 bg-white p-8 text-center text-sm text-stone-500" data-testid={`leg-${i}-empty`}>
                                    No flights match these filters — try clearing them.
                                </div>
                            )}
                            <div className="space-y-3">
                                {list.map((o) => (
                                    <OfferCard key={o.id} offer={o} testid={`offer-leg${i}-${o.id}`}
                                        selected={selected[i]?.id === o.id}
                                        onSelect={(offer) => setSelected({ ...selected, [i]: selected[i]?.id === offer.id ? undefined : offer })}
                                        onRules={openRules} />
                                ))}
                            </div>
                        </section>
                    );
                })}
            </main>

            {/* sticky selection bar */}
            <div className="fixed bottom-0 inset-x-0 z-40 bg-navy text-cream shadow-2xl" data-testid="selection-bar">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <p className="text-[11px] uppercase tracking-wider text-cream/60">
                            {legs.filter((_, i) => selected[i]).length} of {legs.length} flight{legs.length > 1 ? "s" : ""} selected
                        </p>
                        <p className="font-display font-black text-xl">{total ? inr(total) : "—"} <span className="text-xs font-body font-semibold text-cream/60">per adult{data.demo ? " · demo fare" : ""}</span></p>
                    </div>
                    <button data-testid="continue-booking-btn" onClick={proceed} disabled={!allSelected || continuing}
                        className="rounded-full bg-saffron px-6 sm:px-8 py-3 font-display font-black text-white disabled:opacity-40 flex items-center gap-2 hover:bg-gold hover:text-navy transition-colors">
                        {continuing ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="h-4 w-4" /></>}
                    </button>
                </div>
            </div>

            {/* fare rules modal */}
            {(rules || rulesBusy) && (
                <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="fare-rules-modal">
                    <div className="w-full max-w-lg rounded-t-2xl sm:rounded-2xl bg-white p-6 max-h-[85vh] overflow-y-auto">
                        <div className="flex items-center justify-between">
                            <h3 className="font-display font-black text-lg text-navy">Fare rules</h3>
                            <button onClick={() => setRules(null)} data-testid="fare-rules-close" className="text-stone-400 hover:text-navy"><X className="h-5 w-5" /></button>
                        </div>
                        {rulesBusy && <Loader2 className="mt-6 h-6 w-6 animate-spin text-saffron" />}
                        {rules && (
                            <div className="mt-4 space-y-4 text-sm text-stone-600">
                                {rules.demo && <p className="rounded-lg bg-gold/15 border border-gold/60 px-3 py-2 text-xs font-semibold text-navy">Sample rules for demonstration data.</p>}
                                <div><p className="font-bold text-navy mb-1">Cancellation</p><p>{rules.rules.cancellation}</p></div>
                                <div><p className="font-bold text-navy mb-1">Date / flight changes</p><p>{rules.rules.changes}</p></div>
                                <p className="text-xs text-stone-400">{rules.rules.notes}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
